/**
 * useSettingsManagement Hook
 *
 * Manages user settings for stand (movement) and exercise reminders, including:
 * - Enable/disable notifications for movement and exercise
 * - Configure time windows (start/end time, frequency, selected days)
 * - Select exercise preferences (types of exercises)
 * - Snooze functionality for exercise notifications
 * - Persistent storage via API with cache synchronization
 * - Multi-language support with background language data syncing
 *
 * State includes both movement (stand) and exercise settings with dirty-flag tracking
 * for detecting unsaved changes. Each setting category has dedicated update functions
 * and a save handler that syncs to API and refreshes TanStack Query caches.
 *
 * @returns {Object} Settings state, update functions, and UI state management
 */

import { useState, useEffect, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import settingsApiService from "../services/api/SettingsApiService";
import { usePreferencesQuery } from "../stores/tanstack/queries/usePreferencesQuery";
import { useSettingsQuery } from "../stores/tanstack/queries/useSettingsQuery ";
import { useAuthStore } from "../stores/zustand";
import { prefetchAllData } from "./useScheduler";

export const useSettingsManagement = () => {
  // Query client for cache invalidation and refetching
  // Auth context: email and language for API calls and cache keys
  const queryClient = useQueryClient();
  const email = useAuthStore((state) => state.user.email);
  const preferredLanguage = useAuthStore((state) => state.preferredLanguage);

  // Fetch user preferences (exercise types) and saved settings from cache
  const { data: preferencesData, isLoading: isPreferencesLoading } =
    usePreferencesQuery();

  const { data: settingsData, isLoading: isSettingsLoading } =
    useSettingsQuery();

  // Global loading indicator for async operations
  const [isLoading, setIsLoading] = useState(false);

  // Configuration for stand reminder notifications (movement breaks)
  // isDirty flag tracks unsaved changes for save prompt UX
  const [movementSettings, setMovementSettings] = useState({
    isEnabled: false,
    isSnoozeEnabled: false,
    startTime: "09:00",
    endTime: "17:00",
    frequency: "1",
    selectedDays: [],
    isDirty: false,
  });

  // Configuration for exercise reminder notifications
  // Includes preference selection and physio therapist options
  // isDirty flag tracks unsaved changes
  const [exerciseSettings, setExerciseSettings] = useState({
    isEnabled: false,
    isSnoozeEnabled: false,
    selectedTypes: [],
    startTime: "09:00",
    endTime: "17:00",
    frequency: "1",
    selectedDays: [],
    isPhysioEnabled: false,
    isDirty: false,
  });

  // Snooze dialog state for confirming snooze operations
  // Snackbar for success feedback, error state for error messages
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [error, setError] = useState(null);
  const [movementSnoozeDialogOpen, setMovementSnoozeDialogOpen] =
    useState(false);
  const [exerciseSnoozeDialogOpen, setExerciseSnoozeDialogOpen] =
    useState(false);

  // Filter exercise preferences, excluding "Random" type
  const preferences =
    preferencesData?.rawPreferences?.filter((pref) => pref.name !== "Random") ||
    [];

  // Calculate timezone offset for API requests (format: "+02:00" or "-05:00")
  const getTimezoneOffset = () => {
    const offset = new Date().getTimezoneOffset() / -60;
    return offset >= 0
      ? `+${String(Math.abs(offset)).padStart(2, "0")}`
      : `-${String(Math.abs(offset)).padStart(2, "0")}`;
  };

  // Sync saved settings from API to component state when data loads
  // Converts decimal time format from server to HH:MM strings for UI
  useEffect(() => {
    if (settingsData) {
      const { standConfig, excerciseConfig } = settingsData;

      setMovementSettings((prev) => ({
        ...prev,
        startTime: settingsApiService.decimalToTimeString(
          standConfig.startTime
        ),
        endTime: settingsApiService.decimalToTimeString(standConfig.endTime),
        frequency: standConfig.frequency?.toString() || "1",
        selectedDays: standConfig.daysOfWeek || [],
        isEnabled: standConfig.isEnabled,
        isDirty: false,
      }));

      setExerciseSettings((prev) => ({
        ...prev,
        isEnabled: excerciseConfig.isEnabled,
        isSnoozeEnabled: excerciseConfig.isSnoozeEnabled ?? false,
        selectedTypes:
          excerciseConfig.preferences?.map((pref) => pref.id) || [],
        startTime: settingsApiService.decimalToTimeString(
          excerciseConfig.startTime
        ),
        endTime: settingsApiService.decimalToTimeString(
          excerciseConfig.endTime
        ),
        frequency: excerciseConfig.frequency?.toString() || "1",
        selectedDays: excerciseConfig.daysOfWeek || [],
        isPhysioEnabled: excerciseConfig.isPhysioEnabled,
        isDirty: false,
      }));
    }
  }, [settingsData]);

  // Update a single movement setting field and mark as dirty
  const updateMovementSetting = useCallback((field, value) => {
    setMovementSettings((prev) => ({
      ...prev,
      [field]: value,
      isDirty: true,
    }));
  }, []);

  // Toggle a specific day in movement schedule (add/remove)
  const toggleMovementDay = useCallback((day) => {
    setMovementSettings((prev) => {
      const selectedDays = prev.selectedDays.includes(day)
        ? prev.selectedDays.filter((d) => d !== day)
        : [...prev.selectedDays, day];

      return {
        ...prev,
        selectedDays,
        isDirty: true,
      };
    });
  }, []);

  // Save movement settings to API and sync caches
  // Converts HH:MM strings to decimal format, handles timezone offset
  // Refetches both current and other language data
  const saveMovementSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      const config = {
        startTime: settingsApiService.timeStringToDecimal(
          movementSettings.startTime
        ),
        endTime: settingsApiService.timeStringToDecimal(
          movementSettings.endTime
        ),
        frequency: parseFloat(movementSettings.frequency),
        daysOfWeek: movementSettings.selectedDays,
        isEnabled: movementSettings.isEnabled,
        offset: settingsApiService.getFormattedTimezoneOffset(),
      };

      await settingsApiService.updateMovementConfig(config);

      await queryClient.refetchQueries({
        queryKey: ["settings", email],
        exact: true,
      });

      await queryClient.refetchQueries({
        queryKey: ["homeData", email, getTimezoneOffset(), preferredLanguage],
        exact: true,
      });

      const otherLanguage = preferredLanguage === "de" ? "en" : "de";
      await queryClient.refetchQueries({
        queryKey: ["homeData", email, getTimezoneOffset(), otherLanguage],
        exact: true,
      });

      setMovementSettings((prev) => ({ ...prev, isDirty: false }));
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error saving movement settings:", error);
      setError("Failed to save movement settings");
    } finally {
      setIsLoading(false);
    }
  }, [movementSettings, queryClient, email, preferredLanguage]);

  // Update a single exercise setting field and mark as dirty
  const updateExerciseSetting = useCallback((field, value) => {
    setExerciseSettings((prev) => ({
      ...prev,
      [field]: value,
      isDirty: true,
    }));
  }, []);

  // Toggle a specific day in exercise schedule (add/remove)
  const toggleExerciseDay = useCallback((day) => {
    setExerciseSettings((prev) => {
      const selectedDays = prev.selectedDays.includes(day)
        ? prev.selectedDays.filter((d) => d !== day)
        : [...prev.selectedDays, day];

      return {
        ...prev,
        selectedDays,
        isDirty: true,
      };
    });
  }, []);

  // Save exercise settings to API and sync caches
  // Converts time strings to decimal format, includes selected exercise types
  // Refetches current language and background invalidates other language
  const saveExerciseSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      const config = {
        startTime: settingsApiService.timeStringToDecimal(
          exerciseSettings.startTime
        ),
        endTime: settingsApiService.timeStringToDecimal(
          exerciseSettings.endTime
        ),
        frequency: parseFloat(exerciseSettings.frequency),
        daysOfWeek: exerciseSettings.selectedDays,
        isEnabled: exerciseSettings.isEnabled,
        preferences: exerciseSettings.selectedTypes,
        isPhysioEnabled: exerciseSettings.isPhysioEnabled,
        offset: settingsApiService.getFormattedTimezoneOffset(),
      };

      await settingsApiService.updateExerciseConfig(config);

      await queryClient.refetchQueries({
        queryKey: ["settings", email],
        exact: true,
      });

      await queryClient.refetchQueries({
        queryKey: ["homeData", email, getTimezoneOffset(), preferredLanguage],
        exact: true,
      });

      const otherLanguage = preferredLanguage === "de" ? "en" : "de";
      await queryClient.refetchQueries({
        queryKey: ["homeData", email, getTimezoneOffset(), otherLanguage],
        exact: true,
      });

      setExerciseSettings((prev) => ({ ...prev, isDirty: false }));
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error saving exercise settings:", error);
      setError("Failed to save exercise settings");
    } finally {
      setIsLoading(false);
    }
  }, [exerciseSettings, queryClient, email, preferredLanguage]);

  // Initialize exercise settings from partial data (e.g., from onboarding)
  const initializeExerciseSetting = useCallback((partial) => {
    setExerciseSettings((prev) => ({
      ...prev,
      ...partial,
      isDirty: false,
    }));
  }, []);

  // Snooze all exercise notifications and refresh calendar data
  // Shows success feedback and closes dialog on completion
  const snoozeExercise = useCallback(async () => {
    try {
      setIsLoading(true);
      await settingsApiService.snoozeNotifications();
      setExerciseSnoozeDialogOpen(false);

      const timezoneOffset = getTimezoneOffset();
      await prefetchAllData(
        queryClient,
        timezoneOffset,
        preferredLanguage,
        email,
        null
      );

      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error snoozing notifications:", error);
      setError("Failed to snooze notifications");
    } finally {
      setIsLoading(false);
    }
  }, [queryClient, preferredLanguage, email]);

  // Close dialog and error/snackbar handlers
  const closeMovementSnoozeDialog = useCallback(() => {
    setMovementSnoozeDialogOpen(false);
  }, []);

  const closeExerciseSnoozeDialog = useCallback(() => {
    setExerciseSnoozeDialogOpen(false);
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Complete settings management API for components
  // Includes state, handlers, UI state, and computed flags
  return {
    // Exercise preferences list (filtered of "Random" type)
    preferences,
    // Loading states
    isPreferencesLoading,
    isSettingsLoading,
    isLoading,
    // Movement/stand settings
    movementSettings,
    updateMovementSetting,
    toggleMovementDay,
    saveMovementSettings,
    // Exercise settings
    exerciseSettings,
    updateExerciseSetting,
    toggleExerciseDay,
    saveExerciseSettings,
    initializeExerciseSetting,
    snoozeExercise,
    // Dialog state
    movementSnoozeDialogOpen,
    exerciseSnoozeDialogOpen,
    closeMovementSnoozeDialog,
    closeExerciseSnoozeDialog,
    // Feedback state
    snackbarOpen,
    closeSnackbar,
    error,
    clearError,
    // Utility functions
    refreshData: () => {
      queryClient.invalidateQueries(["preferences", email, preferredLanguage]);
      queryClient.invalidateQueries(["settings", email]);
    },
    // Computed flags for UI logic
    hasUnsavedChanges: movementSettings.isDirty || exerciseSettings.isDirty,
    isDataLoaded: !isPreferencesLoading && !isSettingsLoading,
  };
};
